import { Menu, Moon, Sun, X } from "lucide-react";
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import BookCTA from "@/components/BookCTA";
import logo from "@/assets/logo.png";

const navLinks = [
  { label: "Home", href: "/" },
  { label: "Tools", href: "/tools" },
  { label: "Blog", href: "/blog" },
  { label: "Co-Found", href: "/co-found" },
  { label: "About", href: "/about" },
];

const getInitialTheme = (): "dark" | "light" => {
  if (typeof window === "undefined") return "light";
  const stored = localStorage.getItem("theme");
  if (stored === "dark" || stored === "light") return stored;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const Navbar = () => {
  const [isDark, setIsDark] = useState<boolean>(() => getInitialTheme() === "dark");
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const theme = isDark ? "dark" : "light";
    document.documentElement.classList.toggle("dark", isDark);
    localStorage.setItem("theme", theme);
  }, [isDark]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <nav className="brutal-border border-t-0 border-l-0 border-r-0 bg-background sticky top-0 z-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2 font-black text-lg tracking-tight text-foreground">
          <img src={logo} alt="Co-Found with Us logo" width={40} height={40} className="w-10 h-10 rounded-md" />
          <span className="font-black tracking-tight">Co-Found with Us</span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`font-bold text-sm tracking-wide px-4 py-2 rounded-md transition-colors uppercase tracking-[0.08em] ${
                location.pathname === link.href
                  ? "text-primary"
                  : "text-foreground hover:text-primary"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <BookCTA className="hidden sm:inline-flex" size="md" source="navbar">Get Early Access →</BookCTA>

          <button
            onClick={() => setIsDark(!isDark)}
            className="brutal-border brutal-shadow min-w-11 min-h-11 w-11 h-11 flex items-center justify-center bg-card hover:bg-muted transition-colors rounded-md"
            aria-label="Toggle dark mode"
          >
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>

          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden brutal-border brutal-shadow min-w-11 min-h-11 w-11 h-11 flex items-center justify-center bg-card hover:bg-muted transition-colors rounded-md"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          id="mobile-nav"
          className="md:hidden border-t-2 border-foreground bg-background"
        >
          <div className="container mx-auto px-4 py-3 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className={`font-bold text-base px-3 py-3 min-h-11 rounded-md transition-colors uppercase tracking-[0.08em] ${
                  location.pathname === link.href
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground hover:bg-muted"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <BookCTA className="sm:hidden mt-2" size="md" source="navbar-mobile">
              Get Early Access →
            </BookCTA>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
